import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";

import { PaziaInput, PaziaSelect } from "components/atoms";
import { DocumentTypeApi, DocumentTypeModel } from "api/documentTypesApi";
import { PaziaFormButton } from "components/atoms";

interface DocumentTypeFormProps {
  onSubmit: (data: DocumentTypeModel) => Promise<void>;
  documentType?: DocumentTypeApi;
}
export const DocumentTypeForm = ({ onSubmit, documentType }: DocumentTypeFormProps): JSX.Element => {
  const methods = useForm<DocumentTypeFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      [FORM_NAMES.NAME]: documentType?.name ?? "",
      [FORM_NAMES.VALIDITY]: documentType
        ? [0, 365, 730, 1095].includes(documentType.validity)
          ? documentType.validity
          : -1
        : 0,
      [FORM_NAMES.OTHER_VALIDITY]: [undefined, 0, 365, 730, 1095].includes(documentType?.validity)
        ? undefined
        : documentType?.validity,
      [FORM_NAMES.NEXT_NUMBER]: documentType?.nextNumber ?? 1,
    },
  });
  const { handleSubmit, watch } = methods;

  const onSubmitHandler = async (data: DocumentTypeFormValues) => {
    // On edit the number is sent only when it was changed: saving a stale value would rewind a sequence that
    // documents 2.0 issued numbers from in the meantime.
    const nextNumber =
      data.nextNumber != null && data.nextNumber !== documentType?.nextNumber ? data.nextNumber : undefined;
    const finalData = {
      name: data.name,
      validity: data.otherValidity ? data.otherValidity! : data.validity,
      nextNumber,
    };
    await onSubmit(finalData);
    methods.reset();
  };

  const validity = watch(FORM_NAMES.VALIDITY);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitHandler)} className="flex flex-col gap-4">
        <PaziaInput name={FORM_NAMES.NAME} label="Nazwa" type="text" required />
        <PaziaSelect
          name={FORM_NAMES.VALIDITY}
          required
          label="Okres ważności"
          options={[
            { value: 365, label: "Rok" },
            { value: 730, label: "Dwa lata" },
            { value: 1095, label: "Trzy lata" },
            { value: 0, label: "Bezterminowo" },
            { value: OTHER_VALIDITY, label: "Inny" },
          ]}
        />
        {validity == OTHER_VALIDITY && (
          <PaziaInput name={FORM_NAMES.OTHER_VALIDITY} label="Wprowadź okres ważności [dni]" type="number" required />
        )}
        <div className="flex flex-col gap-1">
          <PaziaInput name={FORM_NAMES.NEXT_NUMBER} label="Następny numer" type="number" step="1" />
          <p className="text-xs text-slate-500 dark:text-slate-400">{NEXT_NUMBER_HINT}</p>
        </div>
        <div className="flex justify-end">
          <PaziaFormButton title="Zapisz" />
        </div>
      </form>
    </FormProvider>
  );
};

const OTHER_VALIDITY = -1;
const NEXT_NUMBER_HINT =
  "Kolejny numer nadawany dokumentom 2.0 tego typu. Numery w dokumentach 1.0 są wpisywane ręcznie i system ich nie zna — jeśli ten typ jest używany także w 1.0, ustaw numer powyżej najwyższego już wydanego.";
const FORM_NAMES = {
  NAME: "name",
  VALIDITY: "validity",
  OTHER_VALIDITY: "otherValidity",
  NEXT_NUMBER: "nextNumber",
} as const;

const schema = yup
  .object({
    [FORM_NAMES.NAME]: yup.string().required(),
    [FORM_NAMES.VALIDITY]: yup.number().required(),
    [FORM_NAMES.OTHER_VALIDITY]: yup.number().when(FORM_NAMES.VALIDITY, {
      is: OTHER_VALIDITY,
      then: (shema) => shema.required(),
    }),
    // A cleared field means "leave as is" (default 1 on create).
    [FORM_NAMES.NEXT_NUMBER]: yup
      .number()
      .transform((value, original) => (original === "" || original === null ? undefined : value))
      .integer("Numer musi być liczbą całkowitą.")
      .min(1, "Numer musi być większy od zera.")
      .notRequired(),
  })
  .required();

export type DocumentTypeFormValues = yup.InferType<typeof schema>;
